import cn from 'classnames';
import { nanoid } from 'nanoid';
import React, {
  createRef,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useCombinedRefs } from '../common/Hooks';
import { SignpostPosition, SIGNPOST_POSITIONS } from './constants';
import { calculateLeftAndTopDiff } from './utils';

interface SignpostProps {
  trigger: React.ReactElement<TriggerProps>;
  content: React.ReactElement<ContentProps>;
}

export const Signpost = React.forwardRef<HTMLDivElement, SignpostProps>(
  (props, ref) => {
    const { trigger, content } = props;

    const uidRef = useRef(`clr-id-${nanoid(5)}`);

    const active = content.props.open;

    return (
      <div ref={ref} className="signpost" style={{ position: 'static' }}>
        {React.cloneElement(trigger, { id: uidRef.current, active })}
        {React.cloneElement(content, { id: uidRef.current })}
      </div>
    );
  }
);

interface TriggerProps {
  id?: string;
  active?: boolean;
  children: React.ReactElement;
  onClick: () => void;
}

export const SignpostTrigger: React.FC<TriggerProps> = props => {
  const { id, children, active, onClick } = props;

  const signpostTriggerElement = React.cloneElement(children, {
    class: cn(
      'signpost-trigger',
      children.props.className,
      children.props.class,
      { active }
    ),
    'aria-label': 'Signpost Toggle',
    'aria-expanded': active,
    'aria-controls': id,
    onClick
  });
  return signpostTriggerElement;
};

type ContentProps = PropsWithChildren<{
  id?: string;
  disabledCount?: number;
  open: boolean;
  position?: SignpostPosition;
  onClose: () => void;
}>;

export const SignpostContent = React.forwardRef<HTMLDivElement, ContentProps>(
  (props, ref) => {
    const {
      id,
      disabledCount,
      children,
      open,
      position = 'right-middle',
      onClose
    } = props;

    const contentRef = useRef<HTMLDivElement>(null);
    const combinedRefs = useCombinedRefs(contentRef, ref);
    const signpostContentRef: React.RefObject<HTMLInputElement> = createRef();
    const [
      focusablesignpostContentElements,
      setFocusableSignpostContentElements
    ] = useState<NodeListOf<Element> | null>();
    const [firstElement, setFirstElement] = useState<HTMLElement | null>();
    const [enablefirstElementFocus, setEnableFirstElementFocus] =
      useState(true);

    useEffect(() => {
      if (contentRef.current) {
        contentRef.current.style.position = 'absolute';
        contentRef.current.style.top = '0';
        contentRef.current.style.bottom = 'auto';
        contentRef.current.style.left = '0';
        contentRef.current.style.right = 'auto';
      }
    });

    useEffect(() => {
      if (contentRef.current) {
        const anchor = contentRef.current.parentElement;
        if (anchor) {
          anchor.style.position = 'static';
          const { leftDiff, topDiff } = calculateLeftAndTopDiff(
            anchor,
            contentRef.current,
            SIGNPOST_POSITIONS[position]
          );
          if (Math.round(leftDiff) && Math.round(topDiff))
            contentRef.current.style.transform = `translateX(${Math.round(
              leftDiff
            )}px) translateY(${Math.round(topDiff)}px)`;
        }
      }
    });

    useEffect(() => {
      if (!open) return;
      let signpostContentEls = disabledCount
        ? signpostContentRef.current &&
          signpostContentRef.current.querySelectorAll(
            'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button, [tabindex="0"]'
          )
        : signpostContentRef.current &&
          signpostContentRef.current.querySelectorAll(
            'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]'
          );
      let firstEl =
        signpostContentEls && (signpostContentEls[0] as HTMLElement);
      enablefirstElementFocus && firstEl && firstEl.focus();
      setFocusableSignpostContentElements(signpostContentEls);
      setFirstElement(firstEl);
      setEnableFirstElementFocus(false);
    }, [open, disabledCount]);

    const onKeyDown = (e: React.KeyboardEvent) => {
      e.keyCode === 27
        ? handleEscKey(e)
        : e.keyCode === 9
        ? handleTabKey(e)
        : '';
    };

    const handleEscKey = (e: React.KeyboardEvent) => {
      e.preventDefault();
      onClose && onClose();
      setEnableFirstElementFocus(true);
    };

    const handleTabKey = (e: React.KeyboardEvent) => {
      const lastElement = disabledCount
        ? focusablesignpostContentElements &&
          focusablesignpostContentElements[
            focusablesignpostContentElements.length - (disabledCount + 1)
          ]
        : focusablesignpostContentElements &&
          focusablesignpostContentElements[
            focusablesignpostContentElements.length - 1
          ];

      if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement && (firstElement as HTMLElement).focus();
      }

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement && (lastElement as HTMLElement).focus();
      }
    };

    const handleClickOutside = useCallback(
      (event: any) => {
        if (contentRef.current && !contentRef.current.contains(event.target)) {
          onClose();
          setEnableFirstElementFocus(true);
        }
      },
      [onClose]
    );

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside);
      return function unMount() {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [handleClickOutside]);

    return open ? (
      <div
        ref={combinedRefs}
        className={cn('signpost-content', position)}
        onKeyDown={onKeyDown}
        id={id}>
        <div className="signpost-wrap" ref={signpostContentRef}>
          <div className="popover-pointer" />
          <div className="signpost-content-header">
            <button
              type="button"
              className="signpost-action close"
              onClick={onClose}>
              <clr-icon shape="close" title="Close" />
            </button>
          </div>
          <div className="signpost-content-body">{children}</div>
        </div>
      </div>
    ) : null;
  }
);
